import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarClock, CheckCircle2, FileUp, History, Link2, Lock, PlayCircle, RotateCcw, ShieldCheck, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { STATUS_CLASS, fmtDay } from "@/components/tasks/TaskCard";

const OPEN = ["Open", "In Progress", "Blocked"];
const ATTENDANCE_REASONS = { arrived_late: "Arrived late", absent_with_coverage: "Absent with coverage", absent_without_coverage: "Absent without coverage", clock_in_error: "Clock-in error", other: "Other (add a note)" };

export const TaskDrawer = ({ task, owners, sprints, user, onClose, onChanged }) => {
  const [panel, setPanel] = useState(null);
  const [text, setText] = useState("");
  const [dueDate, setDueDate] = useState(task.due_date);
  const [dueTime, setDueTime] = useState(task.due_time || "");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [edit, setEdit] = useState({ owner_id: task.owner_id, priority: task.priority, notes: task.notes || "", sprint_id: task.sprint_id || "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);
  const isAdmin = user?.role === "system_administrator";
  const isOpen = OPEN.includes(task.status);
  const isClosed = task.status === "Verified" || task.status === "Auto-closed";

  async function run(call, notice) {
    setBusy(true);
    setError("");
    try {
      const response = await call();
      setPanel(null);
      setText("");
      await onChanged(response?.data || null, notice);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  async function uploadEvidence(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append("evidence", file);
    await run(() => api.post(`/tasks/${task.id}/evidence`, body, { headers: { "Content-Type": "multipart/form-data" } }), "Evidence file attached.");
    if (fileInput.current) fileInput.current.value = "";
  }

  async function openEvidence() {
    try {
      const response = await api.get(`/tasks/${task.id}/evidence/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(response.data), "_blank", "noopener");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  const evidence = task.evidence;
  const isAttendance = (task.system_key || "").startsWith("no-clock-in:");
  const [resolution, setResolution] = useState("");
  const attendanceReady = resolution && (resolution !== "other" || text.trim().length >= 3);

  return createPortal(
    <div className="drawer-backdrop" data-testid="task-drawer-backdrop" onClick={onClose}>
      <aside aria-modal="true" className="request-drawer task-drawer" data-testid="task-drawer" role="dialog" onClick={(e) => e.stopPropagation()}>
        <header className="drawer-header">
          <div>
            <p className="drawer-id">{task.source === "system" ? "System-generated task" : task.source === "recurring" ? "Recurring task" : "Task"}</p>
            <h2 data-testid="task-drawer-title">{task.title}</h2>
            <div className="drawer-pills">
              <span className={`status-chip ${STATUS_CLASS[task.status] || ""}`} data-testid="task-drawer-status">{task.status}</span>
              <span className="status-chip status-neutral">{task.priority}</span>
              {task.overdue && <span className="status-chip status-inactive" data-testid="task-drawer-overdue">Overdue</span>}
            </div>
          </div>
          <button className="drawer-close" data-testid="task-drawer-close" type="button" onClick={onClose}><X size={18} /></button>
        </header>
        <div className="drawer-body">
          {error && <p className="form-message error-message" data-testid="task-drawer-error">{error}</p>}
          <section className="drawer-section">
            <div className="drawer-field-grid">
              <div className="drawer-field"><small>Owner</small><span data-testid="task-drawer-owner">{task.owner_name}</span></div>
              <div className="drawer-field"><small>Due</small><span data-testid="task-drawer-due">{fmtDay(task.due_date)}{task.due_time ? ` · ${task.due_time} ET` : ""}</span></div>
              <div className="drawer-field"><small>Linked record</small><span>{task.linked ? <><Link2 size={12} /> {task.linked.label}</> : "—"}</span></div>
              <div className="drawer-field"><small>Sprint</small><span>{sprints.find((s) => s.id === task.sprint_id)?.name || "—"}</span></div>
            </div>
            {task.notes && <p className="drawer-notes" data-testid="task-drawer-notes">{task.notes}</p>}
            {task.blocked_reason && task.status === "Blocked" && <p className="drawer-attention" data-testid="task-drawer-blocked-reason">Blocked: {task.blocked_reason}</p>}
            {task.reopen_comment && task.status === "Open" && <p className="drawer-attention" data-testid="task-drawer-reopen-comment">Reopened by {task.reopened_by_name}: {task.reopen_comment}</p>}
          </section>

          <section className="drawer-section">
            <h3><ShieldCheck size={14} /> Evidence</h3>
            {evidence ? (
              <p className="pairing-meta" data-testid="task-drawer-evidence">
                {evidence.type === "link" ? <a href={evidence.url} target="_blank" rel="noreferrer">{evidence.url}</a> : <button type="button" className="table-edit-button" data-testid="task-evidence-open" onClick={openEvidence}>{evidence.document.original_filename}</button>}
                {" "}· added by {evidence.added_by_name}
              </p>
            ) : (
              <p className="pairing-meta" data-testid="task-drawer-no-evidence">No evidence yet. Marking this task done requires a link or a file.</p>
            )}
            {!isClosed && (
              <label className="table-edit-button credential-upload-button"><FileUp size={13} /> Upload evidence file <input ref={fileInput} data-testid="task-evidence-file-input" type="file" hidden accept="application/pdf,image/png,image/jpeg,image/webp,.docx,.xlsx,.csv" onChange={uploadEvidence} /></label>
            )}
          </section>

          {!isClosed && (
            <section className="drawer-section">
              <h3>Actions</h3>
              <div className="evaluation-action-row">
                {task.status !== "In Progress" && isOpen && <button type="button" className="button button-secondary" data-testid="task-action-start" disabled={busy} onClick={() => run(() => api.post(`/tasks/${task.id}/status`, { status: "In Progress" }), "Task started.")}><PlayCircle size={14} /> Start</button>}
                {isOpen && <button type="button" className="button button-secondary" data-testid="task-action-block" onClick={() => setPanel(panel === "block" ? null : "block")}><Lock size={14} /> Block</button>}
                {isOpen && <button type="button" className="button button-primary" data-testid="task-action-done" onClick={() => setPanel(panel === "done" ? null : "done")}><CheckCircle2 size={14} /> Mark done</button>}
                {task.status === "Blocked" && <button type="button" className="button button-secondary" data-testid="task-action-unblock" disabled={busy} onClick={() => run(() => api.post(`/tasks/${task.id}/status`, { status: "Open" }), "Task unblocked.")}>Unblock</button>}
                {isOpen && <button type="button" className="button button-secondary" data-testid="task-action-reschedule" onClick={() => setPanel(panel === "due" ? null : "due")}><CalendarClock size={14} /> Change due date</button>}
                {isOpen && <button type="button" className="button button-ghost" data-testid="task-action-edit" onClick={() => setPanel(panel === "edit" ? null : "edit")}>Edit</button>}
                {isAdmin && task.status === "Done – Pending Verification" && <button type="button" className="button button-primary" data-testid="task-action-verify" disabled={busy} onClick={() => run(() => api.post(`/tasks/${task.id}/verify`), "Task verified.")}><ShieldCheck size={14} /> Verify</button>}
                {isAdmin && task.status === "Done – Pending Verification" && <button type="button" className="button button-secondary" data-testid="task-action-reopen" onClick={() => setPanel(panel === "reopen" ? null : "reopen")}><RotateCcw size={14} /> Reopen</button>}
              </div>
              {panel === "block" && (
                <div className="evaluation-action-form" data-testid="task-block-form">
                  <label>Reason (required)<textarea data-testid="task-block-reason" rows={2} value={text} onChange={(e) => setText(e.target.value)} /></label>
                  <button type="button" className="button button-primary" data-testid="task-block-submit" disabled={busy || text.trim().length < 3} onClick={() => run(() => api.post(`/tasks/${task.id}/status`, { status: "Blocked", reason: text }), "Task blocked.")}>Mark blocked</button>
                </div>
              )}
              {panel === "done" && isAttendance && (
                <div className="evaluation-action-form" data-testid="task-attendance-close-form">
                  <label>Reason (required)
                    <select data-testid="task-attendance-reason" value={resolution} onChange={(e) => setResolution(e.target.value)}>
                      <option value="">Choose a reason</option>
                      {Object.entries(ATTENDANCE_REASONS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                    </select>
                  </label>
                  <label>Note {resolution === "other" ? "(required)" : "(optional)"}<textarea data-testid="task-attendance-note" rows={2} value={text} onChange={(e) => setText(e.target.value)} /></label>
                  <button type="button" className="button button-primary" data-testid="task-attendance-close-submit" disabled={busy || !attendanceReady} onClick={() => run(() => api.post(`/tasks/${task.id}/status`, { status: "Done – Pending Verification", resolution, reason: text }), "Closed with a reason and sent for verification.")}>Close task</button>
                </div>
              )}
              {panel === "done" && !isAttendance && (
                <div className="evaluation-action-form" data-testid="task-done-form">
                  <label>Evidence link {evidence ? "(optional — a file/link is already attached)" : "(required unless you upload a file above)"}<input data-testid="task-evidence-url-input" type="url" placeholder="https://…" value={evidenceUrl} onChange={(e) => setEvidenceUrl(e.target.value)} /></label>
                  <button type="button" className="button button-primary" data-testid="task-done-submit" disabled={busy || (!evidence && !evidenceUrl.trim())} onClick={() => run(() => api.post(`/tasks/${task.id}/status`, { status: "Done – Pending Verification", evidence_url: evidenceUrl }), "Marked done — sent to the System Administrator for verification.")}>Send for verification</button>
                </div>
              )}
              {panel === "due" && (
                <div className="evaluation-action-form" data-testid="task-due-form">
                  <div className="drawer-form-grid">
                    <label>New due date<input data-testid="task-due-date-new" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></label>
                    <label>Time (ET)<input data-testid="task-due-time-new" type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} /></label>
                  </div>
                  <label>Reason (required)<textarea data-testid="task-due-reason" rows={2} value={text} onChange={(e) => setText(e.target.value)} /></label>
                  <button type="button" className="button button-primary" data-testid="task-due-submit" disabled={busy || text.trim().length < 3 || !dueDate} onClick={() => run(() => api.post(`/tasks/${task.id}/due-date`, { due_date: dueDate, due_time: dueTime, reason: text }), "Due date changed and logged.")}>Save due date</button>
                </div>
              )}
              {panel === "reopen" && (
                <div className="evaluation-action-form" data-testid="task-reopen-form">
                  <label>Comment for the owner (required)<textarea data-testid="task-reopen-comment" rows={2} value={text} onChange={(e) => setText(e.target.value)} /></label>
                  <button type="button" className="button button-primary" data-testid="task-reopen-submit" disabled={busy || text.trim().length < 3} onClick={() => run(() => api.post(`/tasks/${task.id}/reopen`, { comment: text }), "Task reopened and returned to the owner.")}>Reopen task</button>
                </div>
              )}
              {panel === "edit" && (
                <div className="evaluation-action-form" data-testid="task-edit-form">
                  <div className="drawer-form-grid">
                    <label>Owner<select data-testid="task-edit-owner" value={edit.owner_id} onChange={(e) => setEdit({ ...edit, owner_id: e.target.value })}>{owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></label>
                    <label>Priority<select data-testid="task-edit-priority" value={edit.priority} onChange={(e) => setEdit({ ...edit, priority: e.target.value })}>{["High", "Normal", "Low"].map((p) => <option key={p}>{p}</option>)}</select></label>
                    <label>Sprint<select data-testid="task-edit-sprint" value={edit.sprint_id} onChange={(e) => setEdit({ ...edit, sprint_id: e.target.value })}><option value="">No sprint</option>{sprints.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
                  </div>
                  <label>Notes<textarea data-testid="task-edit-notes" rows={3} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></label>
                  <button type="button" className="button button-primary" data-testid="task-edit-submit" disabled={busy} onClick={() => run(() => api.patch(`/tasks/${task.id}`, edit), "Task updated.")}>Save changes</button>
                </div>
              )}
            </section>
          )}

          <section className="drawer-section">
            <h3><History size={14} /> Change log</h3>
            <ol className="drawer-timeline" data-testid="task-history">
              {(task.history || []).slice().reverse().map((event) => (
                <li key={event.id}>
                  <strong>{event.action}</strong>
                  {event.detail && <p>{event.detail}</p>}
                  <small>{event.by_name} · {new Date(event.at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</small>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </aside>
    </div>,
    document.body,
  );
};
